import { budgetAlert, BudgetFeedback } from "./BudgetAlert";
import React, { useState, useEffect } from 'react';
import { useBudgetCategories } from "./budgetCategories";


const initialFormState = {
    ItemName: '',
    ItemType: 1,
    ItemCategory: 'ครุภัณฑ์',
    ReplacementQty: 0,
    NewPurchaseQty: 0,
    UnitPrice: 0,
    
    // รายละเอียดประกอบ
    ExistingQty: 0,
    TotalRequiredQty: 0,
    OldItemAge: '',
    Reason: '',
    
    // Checklist เอกสารแนบ
    IsStandardPrice: false,
    HasSpec: false,
    HasQuotation: false,
    HasTechAssessment: false,
    HasBlueprint: false,
    HasBOQ: false
};

const RequestItemForm = ({ onAdd, selectedItemType = 1, subcategoryCounts = {}, onSubcategoryChange, readOnly = false }) => {
    const { getCategoryLabel, isCapitalCategory, getOperatingSubcategories, loading, error } = useBudgetCategories();
    const initialCategory = type => getOperatingSubcategories(type).length ? '' : getCategoryLabel(type);
    const [formData, setFormData] = useState({
        ...initialFormState,
        ItemType: selectedItemType,
        ItemCategory: initialCategory(selectedItemType)
    });

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            ItemType: selectedItemType,
            ItemCategory: initialCategory(selectedItemType),
            ReplacementQty: isCapitalCategory(selectedItemType) ? prev.ReplacementQty : 0
        }));
    }, [selectedItemType, loading]);

    useEffect(() => {
        if (onSubcategoryChange) onSubcategoryChange({ categoryKey: selectedItemType, label: formData.ItemCategory });
    }, [selectedItemType, formData.ItemCategory, onSubcategoryChange]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ 
            ...formData, 
            [name]: type === 'checkbox' ? checked : value 
        });
    };

    const handleAddClick = () => {
        if (getOperatingSubcategories(selectedItemType).length && !getOperatingSubcategories(selectedItemType).includes(formData.ItemCategory)) {
            return budgetAlert.warning("กรุณาเลือกหมวดย่อยของรายการ");
        }
        if (!formData.ItemName.trim()) return budgetAlert.warning("กรุณาระบุชื่อรายการ");
        if (parseFloat(formData.UnitPrice || 0) <= 0) return budgetAlert.warning("กรุณาระบุราคาต่อหน่วย");
        
        const repQty = isCapitalCategory(selectedItemType) ? parseInt(formData.ReplacementQty || 0) : 0;
        const newQty = parseInt(formData.NewPurchaseQty || 0);
        const reqQty = repQty + newQty;

        if (reqQty <= 0) {
            return budgetAlert.warning(isCapitalCategory(selectedItemType) ? "กรุณาระบุจำนวนขอทดแทน หรือ ซื้อใหม่อย่างน้อย 1 หน่วย" : "กรุณาระบุจำนวนที่ต้องการอย่างน้อย 1 หน่วย");
        }

        onAdd({
            ...formData,
            ItemType: selectedItemType,
            ItemCategory: formData.ItemCategory,
            ReplacementQty: repQty,
            NewPurchaseQty: newQty,
            RequestQty: reqQty,
            TotalAmount: reqQty * parseFloat(formData.UnitPrice || 0)
        });

        // ล้างรายละเอียดรายการ โดยคงหมวดย่อยที่เลือกไว้สำหรับรายการถัดไป
        setFormData(prev => ({
            ...initialFormState,
            ItemType: selectedItemType,
            ItemCategory: prev.ItemCategory
        }));
    };

    if (error) return <BudgetFeedback message={error} />;
    if (loading) return <div>กำลังโหลดหมวดงบประมาณ...</div>;

    const isDurableGoods = isCapitalCategory(selectedItemType);
    const subcategories = getOperatingSubcategories(selectedItemType);

    return (
        <div style={{ padding: '15px', border: '1px solid #00acc1', borderRadius: '8px', backgroundColor: '#e0f7fa' }}>
            {subcategories.length > 0 && (
                <div className="row mb-3">
                    <div className="col-md-6">
                        <label className="font-weight-bold">หมวดย่อย{getCategoryLabel(selectedItemType)} *</label>
                        <select name="ItemCategory" aria-label="หมวดย่อยรายการใหม่" className="form-control"
                            value={formData.ItemCategory} onChange={handleChange}>
                            <option value="">-- เลือกหมวดย่อย --</option>
                            {subcategories.map(label => <option key={label} value={label}>{label} ({subcategoryCounts[label] || 0})</option>)}
                        </select>
                    </div>
                </div>
            )}
            {!readOnly && <div className="row align-items-end">
                {/* หมวดงบลงทุนที่เลือกจากฟอร์มหลัก */}
                {isDurableGoods && (
                    <div className="col-md-2">
                        <label className="font-weight-bold" style={{ fontSize: '12px' }}>หมวดหมู่</label>
                        <input className="form-control form-control-sm" value={getCategoryLabel(selectedItemType)} readOnly />
                    </div>
                )}

                {/* 2. ชื่อรายการ (ปรับความกว้างตามกรณี) */}
                <div className={isDurableGoods ? "col-md-4" : "col-md-5"}>
                    <label className="font-weight-bold" style={{ fontSize: '12px' }}>ชื่อรายการ *</label>
                    <input 
                        type="text" 
                        name="ItemName" 
                        aria-label="ชื่อรายการใหม่"
                        className="form-control form-control-sm" 
                        placeholder={`ระบุรายการ${getCategoryLabel(selectedItemType)}`}
                        value={formData.ItemName} 
                        onChange={handleChange} 
                    />
                </div>

                {/* จำนวนทดแทนสำหรับรายการงบลงทุน */}
                {isDurableGoods && (
                    <div className="col-md-1">
                        <label className="text-warning font-weight-bold" style={{ fontSize: '12px' }}>ทดแทน</label>
                        <input 
                            type="number" 
                            name="ReplacementQty" 
                            className="form-control form-control-sm" 
                            value={formData.ReplacementQty} 
                            onChange={handleChange} 
                        />
                    </div>
                )}

                {/* 4. จำนวนที่ต้องการ / ซื้อใหม่ */}
                <div className={isDurableGoods ? "col-md-1" : "col-md-2"}>
                    <label className="text-success font-weight-bold" style={{ fontSize: '12px' }}>
                        {isDurableGoods ? "ซื้อใหม่" : "จำนวนที่ขอ"}
                    </label>
                    <input 
                        type="number" 
                        name="NewPurchaseQty" 
                        aria-label="จำนวนที่ขอใหม่"
                        className="form-control form-control-sm" 
                        value={formData.NewPurchaseQty} 
                        onChange={handleChange} 
                    />
                </div>

                {/* 5. ราคาต่อหน่วย */}
                <div className={isDurableGoods ? "col-md-2" : "col-md-3"}>
                    <label className="font-weight-bold" style={{ fontSize: '12px' }}>ราคา/หน่วย</label>
                    <input 
                        type="number" 
                        name="UnitPrice" 
                        aria-label="ราคาต่อหน่วยใหม่"
                        className="form-control form-control-sm" 
                        value={formData.UnitPrice} 
                        onChange={handleChange} 
                    />
                </div>

                {/* 6. ปุ่มเพิ่ม */}
                <div className="col-md-2">
                    <button type="button" className="btn btn-info btn-sm w-100" onClick={handleAddClick}>
                        <i className="fas fa-plus"></i> เพิ่มเข้าตาราง
                    </button>
                </div>
            </div>}
        </div>
    );
};

export default RequestItemForm;
